
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { handleError, initClient } from '../../api';
import { WindowType } from '../../util';
import {
  Activity,
  ActivityControllerUIClient,
  ActivityLinkMediaRequest,
  ActivityPriority,
  Duty,
  ListItem,
  Media,
  MediaApiControllerUIClient,
} from '../backend/backendapi';
import DescriptionEditModal from './descriptioneditmodal/descriptioneditmodal.controller';
import ReferenceEditModal from './referenceeditmodal/referenceeditmodal.controller';
import DropZone from '../dropzone/DropZone';

declare var window: WindowType;

@Component({
  components: {
    DropZone,
    ImageViewer: require('../imageviewer/imageviewer.vue.html').default,
    DescriptionEditModal: require('./descriptioneditmodal/descriptioneditmodal.vue.html').default,
    PrioritySelectModal: require('./priorityselectmodal/priorityselectmodal.vue.html').default,
    ReferenceEditModal: require('./referenceeditmodal/referenceeditmodal.vue.html').default,
    UserSelectModal: require('./userselectmodal/userselectmodal.vue.html').default,
  },
})
export default class TabInfo extends Vue {
  @Prop({ required: true })
  readonly duty!: Duty;
  @Prop({ required: true })
  readonly activity!: Activity;
  @Prop({ required: true })
  readonly availableResponsibles!: ListItem[];
  @Prop({ required: true })
  readonly availablePriorities!: ActivityPriority[];

  //api
  private readonly activityAPI = initClient(ActivityControllerUIClient);
  private readonly mediaApi = initClient(MediaApiControllerUIClient);

  get baseUrl(): string {
    console.log(window.application.baseUrl);
    return window.location.protocol + '//' + window.location.host + window.application.baseUrl;
  }

  get priorityBadgeStyle(): object {
    let bg = this.activity.priority?.colorTag ?? '000000';
    if (bg.startsWith('#')) {
      bg = bg.substring(1);
    }
    const brightness = bg.length == 3
        ? (parseInt(bg.substring(0, 1), 16) + parseInt(bg.substring(1, 2), 16) + parseInt(bg.substring(2, 3), 16)) * 11
        : parseInt(bg.substring(0, 2), 16) + parseInt(bg.substring(2, 4), 16) + parseInt(bg.substring(4, 6), 16);
    const color = brightness > 127 * 3 ? '000000' : 'ffffff';
    return {
      color: '#' + color,
      'background-color': '#' + bg,
    };
  }

  get selectedResponsible(): ListItem | null {
    return this.availableResponsibles.find(e => e.id === this.activity.fkUserIdResponsible) ?? null;
  }

  descriptionSaved(description: string): void {
    this.activity.description = description;
    this.activityAPI.updateDescription(this.activity.id!, this.activity.description)
        .then(e => {
          this.$toasted.info(this.$t('txtSaved').toString());
          (this.$refs.descriptioneditmodal as DescriptionEditModal).closeModal();
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
    this.notifyChange();
  }

  priorityChanged(priority: ActivityPriority | undefined): void {
    this.activity.priority = priority;
    this.activityAPI.updatePriority(this.activity.id!, this.activity.priority?.id)
        .then(e => this.$toasted.info(this.$t('txtSaved').toString()))
        .catch(e => handleError(this.$toasted, this.$i18n, e));
    this.notifyChange();
  }

  referenceSaved(reference: string): void {
    this.activity.reference = reference;
    this.activityAPI.updateReference(this.activity.id!, this.activity.reference)
        .then(e => {
          this.$toasted.info(this.$t('txtSaved').toString());
          (this.$refs.referenceeditmodal as ReferenceEditModal).closeModal();
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
    this.notifyChange();
  }

  responsibleChanged(user: ListItem | null): void {
    this.activity.fkUserIdResponsible = user?.id;
    this.activity.fkUserResponsibleName = user?.userName;
    this.activityAPI.updateResponsible(this.activity.id!, this.activity.fkUserIdResponsible)
        .then(e => this.$toasted.info(this.$t('txtSaved').toString()))
        .catch(e => handleError(this.$toasted, this.$i18n, e));
    this.notifyChange();
  }

  async onDzComplete(file: any): Promise<void> {
    if (file.status === 'success' && file.xhr.responseText !== '') {
      const response = JSON.parse(file.xhr.responseText);
      const media = await this.mediaApi.get(response.fileId);
      await this.activityAPI.linkMedia(new ActivityLinkMediaRequest({
        activityId: this.activity.id!,
        url: media.publicUrl!,
        fileSize: file.size,
        mediaId: media.id,
      }));

      let newFile = new Media();
      newFile.id = response.fileId;
      newFile.contentType = 'NA';
      newFile.filename = file.name;
      newFile.size = file.size;
      newFile.created = file.lastModifiedDate;
      if (!this.activity.file) {
        this.activity.file = [];
      }
      this.activity.file.push(newFile);
      console.log('file upload ok');
    }
    if (file.status !== 'success' && file.xhr.responseText !== '') {
      const response = JSON.parse(file.xhr.responseText);
      file.previewElement.querySelector('[data-dz-errormessage]').innerText = response.body;
    }
  }

  private get canSetResponsiblePerson(): boolean {
    return window.application.permissions.activity.setResponsiblePerson;
  }

  private notifyChange(): void {
    this.$emit('change');
  }
}
